import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Pin, PinOff, StickyNote, Search, ListChecks, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { NOTE_COLORS, colorBg } from "@/lib/colors";

const empty = { title: "", content: "", color: "yellow", pinned: false, checklist: [] };

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/notes");
      setNotes(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...empty, checklist: [] }); setEditing(null); setOpen(true); };
  const openEdit = (n) => {
    setForm({ title: n.title || "", content: n.content || "", color: n.color || "yellow",
      pinned: !!n.pinned, checklist: (n.checklist || []).map((c) => ({ ...c })) });
    setEditing(n.id); setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim() && !form.content.trim() && !(form.checklist || []).some((c) => c.text.trim())) {
      toast.error("Escribe un título, contenido o una tarea"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, checklist: (form.checklist || []).filter((c) => c.text.trim()) };
      if (editing) await api.put(`/notes/${editing}`, payload);
      else await api.post("/notes", payload);
      toast.success(editing ? "Nota actualizada" : "Nota creada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (n) => {
    const pinned = !n.pinned;
    setNotes((prev) => prev.map((x) => x.id === n.id ? { ...x, pinned } : x));
    try {
      await api.put(`/notes/${n.id}`, { title: n.title, content: n.content, color: n.color, pinned, checklist: n.checklist || [] });
      load();
    } catch (e) { toast.error("No se pudo fijar"); }
  };

  const toggleCheck = async (n, idx) => {
    const checklist = (n.checklist || []).map((c, i) => i === idx ? { ...c, done: !c.done } : c);
    setNotes((prev) => prev.map((x) => x.id === n.id ? { ...x, checklist } : x));
    try {
      await api.put(`/notes/${n.id}`, { title: n.title, content: n.content, color: n.color, pinned: n.pinned, checklist });
    } catch (e) { toast.error("No se pudo actualizar la tarea"); load(); }
  };

  // Checklist editor helpers (inside the dialog form)
  const addCheckItem = () => setForm((f) => ({ ...f, checklist: [...(f.checklist || []), { text: "", done: false }] }));
  const updateCheckText = (i, text) => setForm((f) => ({ ...f, checklist: f.checklist.map((c, idx) => idx === i ? { ...c, text } : c) }));
  const toggleCheckForm = (i) => setForm((f) => ({ ...f, checklist: f.checklist.map((c, idx) => idx === i ? { ...c, done: !c.done } : c) }));
  const removeCheckItem = (i) => setForm((f) => ({ ...f, checklist: f.checklist.filter((_, idx) => idx !== i) }));

  const confirmDelete = async () => {
    try {
      await api.delete(`/notes/${toDelete.id}`);
      toast.success("Nota eliminada");
      setToDelete(null);
      load();
    } catch (e) { toast.error("No se pudo eliminar"); }
  };

  const q = search.trim().toLowerCase();
  const visible = q
    ? notes.filter((n) => (n.title || "").toLowerCase().includes(q) || (n.content || "").toLowerCase().includes(q))
    : notes;

  return (
    <div className="space-y-6" data-testid="notes-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Notas</h1>
          <p className="text-muted-foreground mt-1">{notes.length} en total</p>
        </div>
        <Button onClick={openNew} data-testid="new-note-button">
          <Plus className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Nueva nota</span>
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : notes.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-10 text-center text-muted-foreground">
          <StickyNote className="h-8 w-8 mx-auto mb-2 opacity-40" />
          No hay notas todavía. Crea la primera.
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Buscar por título o contenido…" value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="note-search" />
          </div>
          {visible.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-10 text-center text-muted-foreground">
              No hay notas que coincidan con “{search}”.
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 [&>*]:mb-4" data-testid="notes-list">
              {visible.map((n) => (
            <div key={n.id} data-testid={`note-item-${n.id}`}
              style={{ backgroundColor: colorBg(NOTE_COLORS, n.color) }}
              className="break-inside-avoid rounded-lg border border-black/5 shadow-sm p-4 flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display font-semibold text-base break-words min-w-0" data-testid={`note-title-${n.id}`}>
                  {n.title || <span className="text-black/40 italic">Sin título</span>}
                </h3>
                <button onClick={() => togglePin(n)} data-testid={`note-pin-${n.id}`}
                  className="shrink-0 text-black/50 hover:text-black transition-colors" title={n.pinned ? "Quitar fijado" : "Fijar"}>
                  {n.pinned ? <Pin className="h-4 w-4 fill-current" /> : <PinOff className="h-4 w-4" />}
                </button>
              </div>
              {n.content && (
                <p className="text-sm text-black/80 whitespace-pre-wrap break-words" data-testid={`note-content-${n.id}`}>
                  {n.content}
                </p>
              )}
              {(n.checklist || []).length > 0 && (
                <div className="space-y-1 mt-0.5" data-testid={`note-checklist-${n.id}`}>
                  {n.checklist.map((c, idx) => (
                    <label key={idx} className="flex items-start gap-2 text-sm text-black/80 cursor-pointer">
                      <Checkbox checked={c.done} onCheckedChange={() => toggleCheck(n, idx)}
                        data-testid={`note-check-${n.id}-${idx}`} className="mt-0.5 border-black/30" />
                      <span className={c.done ? "line-through text-black/40" : ""}>{c.text}</span>
                    </label>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-end gap-1 pt-1 mt-auto">
                <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-black/5" onClick={() => openEdit(n)} data-testid={`edit-note-${n.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-black/5" onClick={() => setToDelete(n)} data-testid={`delete-note-${n.id}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="note-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar nota" : "Nueva nota"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Título</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="note-title-input" placeholder="Título de la nota" />
            </div>
            <div>
              <Label>Contenido</Label>
              <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                data-testid="note-content-input" rows={4} placeholder="Anota aquí los detalles del trabajo…" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5"><ListChecks className="h-4 w-4" /> Lista de tareas</Label>
                <Button type="button" size="sm" variant="ghost" onClick={addCheckItem} data-testid="note-add-check">
                  <Plus className="h-4 w-4 mr-1" /> Añadir tarea
                </Button>
              </div>
              <div className="space-y-2 mt-1">
                {(form.checklist || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`note-check-row-${i}`}>
                    <Checkbox checked={c.done} onCheckedChange={() => toggleCheckForm(i)} data-testid={`note-check-toggle-${i}`} />
                    <Input value={c.text} onChange={(e) => updateCheckText(i, e.target.value)}
                      data-testid={`note-check-text-${i}`} placeholder="Tarea a realizar…" className="h-8" />
                    <button type="button" onClick={() => removeCheckItem(i)} data-testid={`note-check-remove-${i}`}
                      className="text-muted-foreground hover:text-destructive shrink-0">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {(form.checklist || []).length === 0 && (
                  <p className="text-xs text-muted-foreground">Sin tareas. Pulsa “Añadir tarea” para crear una lista.</p>
                )}
              </div>
            </div>
            <div>
              <Label>Color de fondo</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {NOTE_COLORS.map((c) => (
                  <button key={c.key} type="button" onClick={() => setForm({ ...form, color: c.key })}
                    title={c.label} data-testid={`note-color-${c.key}`}
                    className={`h-8 w-8 rounded-full border-2 transition-transform ${form.color === c.key ? "border-primary scale-110" : "border-border"}`}
                    style={{ backgroundColor: c.bg }} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="save-note-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-note-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta nota?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-note"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
